import React from "react";
import moment, { Moment } from "moment-timezone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DateTimePicker,
  TimePicker,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { useTheme } from "@mui/material";
import { STANDARD_APP_DATE_TIME_FORMAT, STANDARD_APP_TIME_FORMAT } from "../../constants/constants";

export const CustomDatePicker: React.FC<{
  value: string;
  sx?: any;
  disabled?: boolean;
  views?: ("day" | "month" | "year")[];
  inputFormat?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  onChange: (
    value: any,
    timeWithSeconds: any,
    momentObject: Moment | null
  ) => void;
  disablePast?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  // Optional z-index for the calendar popup. Needed when the picker lives
  // inside a high-zIndex Drawer/Dialog, otherwise the popup portals to the
  // body at the default modal zIndex (1300) and opens *behind* the drawer.
  popupZIndex?: number;
}> = (props) => {
  const {
    value,
    sx,
    views = ["year", "month", "day"],
    inputFormat = "MM-DD-YYYY",
    fullWidth,
    disabled,
    errorMessage,
    disablePast = false,
    onBlur,
    popupZIndex,
  } = props;
  const theme = useTheme();

  const handleDateChange = (newValue: Moment | null) => {
    if (newValue && newValue.isValid()) {
      const newDate = moment(newValue).format("YYYY-MM-DD");
      const newDate1 = moment(newValue).format("YYYY-MM-DD, HH:mm:ss a");
      props.onChange(newDate, newDate1, newValue);
    } else if (newValue === null) {
      props.onChange(null, null, null);
    }
  };

  return (
    <>
      <DatePicker
        views={views}
        disabled={disabled}
        format={inputFormat}
        minDate={disablePast ? moment().startOf("day") : undefined}
        sx={{
          width: fullWidth ? "100%" : "unset",
          "& .MuiOutlinedInput-input": {
            padding: "9.7px 13px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            [theme.breakpoints.up("md")]: {
              padding: "9.8px 13px",
            },
            [theme.breakpoints.up("lg")]: {
              padding: "9.7px 13px",
            },
            [theme.breakpoints.up("xl")]: {
              padding: "11.5px 13px",
            },
          },
          ...sx,
        }}
        value={value ? moment(value) : null}
        slotProps={{
          ...(popupZIndex
            ? {
                popper: { sx: { zIndex: popupZIndex } },
                mobilePaper: { sx: { zIndex: popupZIndex } },
              }
            : {}),
          textField: {
            onBlur: (e) => {
              // Handle manual typing completion on blur
              const typedValue = e.target.value;
              if (typedValue) {
                const parsedDate = moment(typedValue, inputFormat, true);
                if (parsedDate.isValid()) {
                  handleDateChange(parsedDate);
                }
              }
              if (onBlur) {
                onBlur(e);
              }
            },
          },
        }}
        onAccept={handleDateChange}
      />
      {errorMessage && <ErrorMessage value={errorMessage} />}
    </>
  );
};

export const CustomShrinkDatePicker: React.FC<{
  value: string;
  label: string;
  sx?: any;
  disabled?: boolean;
  views?: ("day" | "month" | "year")[];
  inputFormat?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  size?: "small" | "medium";
  onChange: (
    value: any,
    timeWithSeconds: any,
    momentObject: Moment | null
  ) => void;
}> = (props) => {
  const {
    value,
    sx,
    views = ["year", "month", "day"],
    inputFormat = "MM-DD-YYYY",
    fullWidth,
    disabled,
    size,
    errorMessage,
  } = props;

  const handleDateChange = (newValue: Moment | null) => {
    if (!newValue || !newValue.isValid()) {
      props.onChange(null, null, null);
      return;
    }
  
    let formattedValue: string;
  
    if (views.includes("month") && !views.includes("day")) {
      formattedValue = newValue.format("MM");
    } else if (views.includes("year") && !views.includes("month")) {
      formattedValue = newValue.format("YYYY");
    } else {
      formattedValue = newValue.format("YYYY-MM-DD");
    }
  
    props.onChange(
      formattedValue,
      newValue.format("YYYY-MM-DD HH:mm:ss"),
      newValue
    );
  };
  
  // ✅ Safe internal value
  const pickerValue = value
    ? views.includes("month") && !views.includes("day")
      ? moment(`2000-${value}-01`, "YYYY-MM-DD", true)
      : views.includes("year") && !views.includes("month")
        ? moment(`${value}-01-01`, "YYYY-MM-DD", true)
        : moment(value, "YYYY-MM-DD", true)
    : null;
  
  return (
    <>
      <DatePicker
        views={views}
        label={props.label}
        disabled={disabled}
        format={inputFormat}
        value={pickerValue}
        onChange={handleDateChange}   // ✅ KEY FIX
        sx={{
          width: fullWidth ? "100%" : "unset",
          ...sx,
        }}
        slotProps={{
          textField: {
            size: size || "medium",
            error: !!errorMessage,
          },
        }}
      />
      {errorMessage && <ErrorMessage value={errorMessage} />}
    </>
  );
};

export const CustomTimePicker: React.FC<{
  value: string | null;
  sx?: any;
  fullWidth?: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}> = (props) => {
  const { value, sx, fullWidth, disabled, onBlur } = props;
  const timeString = value;
  const momentObject = moment(timeString, STANDARD_APP_TIME_FORMAT);

  const handleTimeChange = (newValue: Moment | null) => {
    if (newValue && newValue.isValid()) {
      props.onChange(newValue.format(STANDARD_APP_TIME_FORMAT));
    }
  };

  return (
    <TimePicker
      value={momentObject}
      disabled={disabled}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
      slotProps={{
        textField: {
          onBlur: (e) => {
            const typedValue = e.target.value;
            if (typedValue) {
              const formats = [
                'h:mm A',      // 8:45 PM
                'h:mm a',      // 8:45 pm
                'hh:mm A',     // 08:45 PM
                'hh:mm a',     // 08:45 pm
                'H:mm',        // 20:45 (24-hour)
                'HH:mm',       // 20:45 (24-hour)
                STANDARD_APP_TIME_FORMAT 
              ];
              
              const parsedTime = moment(typedValue, formats, true);
              
              if (parsedTime.isValid()) {
                handleTimeChange(parsedTime);
              }
            }
            if (onBlur) {
              onBlur(e);
            }
          },
        },
      }}
      onAccept={handleTimeChange}
      sx={{
        width: fullWidth ? "100%" : "unset",
        "& .MuiOutlinedInput-input": {
          padding: "9px 13px",
          fontSize: "0.8rem",
          color: "rgb(38, 38, 38)",
        },
        ...sx,
      }}
    />
  );
};

interface CustomDateAndTimePickerProps {
  value: string | null;
  sx?: any;
  disabled?: boolean;
  inputFormat?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  onChange: (
    value: string | null,
    timeWithSeconds: string | null,
    momentObject: Moment | null
  ) => void;
}

export const CustomDateAndTimePicker: React.FC<CustomDateAndTimePickerProps> = (
  props
) => {
  const {
    value,
    sx,
    inputFormat = "MM-DD-YYYY hh:mm A",
    fullWidth,
    disabled,
    errorMessage,
    onChange,
  } = props;

  const theme = useTheme();

  const handleDateTimeChange = (newValue: Moment | null) => {
    if (newValue && newValue.isValid()) {
      const newDate = moment(newValue).format(STANDARD_APP_DATE_TIME_FORMAT);
      const newDate1 = moment(newValue).format("YYYY-MM-DD, HH:mm:ss a");
      onChange(newDate, newDate1, newValue);
    } else if (newValue === null) {
      onChange(null, null, null);
    }
  };

  return (
    <>
      <DateTimePicker
        disabled={disabled}
        format={inputFormat}
        sx={{
          width: fullWidth ? "100%" : "unset",
          "& .MuiOutlinedInput-input": {
            padding: "9.7px 13px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            [theme.breakpoints.up("md")]: { padding: "9.8px 13px" },
            [theme.breakpoints.up("lg")]: { padding: "9.7px 13px" },
            [theme.breakpoints.up("xl")]: { padding: "11.5px 13px" },
          },
          ...sx,
        }}
        value={value ? moment(value) : null}
        slotProps={{
          textField: {
            onBlur: (e) => {
              const typedValue = e.target.value;
              if (typedValue) {
                const parsedDateTime = moment(typedValue, inputFormat, true);
                if (parsedDateTime.isValid()) {
                  handleDateTimeChange(parsedDateTime);
                }
              }
            },
          },
        }}
        onAccept={handleDateTimeChange}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        }}
      />
      {errorMessage && <ErrorMessage value={errorMessage} />}
    </>
  );
};
